import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_API_URL}/api`;

const THEME_CLASS = {
  info: "bg-sky-500/15 border-sky-500/30 text-sky-100",
  success: "bg-emerald-500/15 border-emerald-500/30 text-emerald-100",
  warning: "bg-amber-500/15 border-amber-500/30 text-amber-100",
  danger: "bg-red-500/15 border-red-500/30 text-red-100",
  promo: "bg-violet-500/15 border-violet-500/30 text-violet-100",
};

export default function AnnouncementBar() {
  const { user } = useAuth();
  const [announcement, setAnnouncement] = useState(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        const res = await axios.get(`${API}/announcements/active`, {
          withCredentials: true,
        });
        if (!mounted) return;
        const data = res.data || null;
        setAnnouncement(data);
      } catch {
        if (!mounted) return;
        setAnnouncement(null);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [user?.user_id, user?.plan]);

  useEffect(() => {
    if (!announcement?.id) {
      setHidden(false);
      return;
    }
    const dismissed = localStorage.getItem(`veriksa_announcement_dismissed_${announcement.id}`);
    setHidden(Boolean(announcement.dismissible && dismissed));
  }, [announcement?.id, announcement?.dismissible]);

  const themeClass = useMemo(() => {
    return THEME_CLASS[announcement?.theme] || THEME_CLASS.info;
  }, [announcement?.theme]);

  const handleDismiss = () => {
    if (announcement?.id) {
      localStorage.setItem(`veriksa_announcement_dismissed_${announcement.id}`, "1");
    }
    setHidden(true);
  };

  if (!announcement || hidden) return null;

  const ctaUrl = String(announcement.cta_url || "");
  const isExternal = /^https?:\/\//i.test(ctaUrl);

  return (
    <div className={`w-full border-b ${themeClass}`}>
      <div className="max-w-7xl mx-auto h-10 px-4 md:px-8 flex items-center justify-between gap-3">
        <div className="min-w-0 flex items-center gap-3 text-sm">
          <span className="truncate">{announcement.message}</span>
          {announcement.cta_text && announcement.cta_url && (
            isExternal ? (
              <a
                href={ctaUrl}
                target="_blank"
                rel="noreferrer"
                className="shrink-0 text-xs font-semibold px-2 py-1 rounded border border-current/40 hover:bg-white/10 transition-colors"
              >
                {announcement.cta_text}
              </a>
            ) : (
              <Link
                to={ctaUrl}
                className="shrink-0 text-xs font-semibold px-2 py-1 rounded border border-current/40 hover:bg-white/10 transition-colors"
              >
                {announcement.cta_text}
              </Link>
            )
          )}
        </div>
        {announcement.dismissible && (
          <button
            type="button"
            onClick={handleDismiss}
            className="shrink-0 p-1 rounded hover:bg-white/10 transition-colors"
            aria-label="Tutup pengumuman"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
